import { IconType } from "@platform/ui/components";
import * as React from "react";
import { NavigateOptions, useSearchParams } from "react-router";
import { z } from "zod";
import { PaginationLimitOptionSchema } from "~/shared/components/ds/Pagination/Pagination";
import { DEFAULT_NAVIGATE_OPTIONS } from "~/shared/constants/default-navigate-options.constants";
import { useSharedTransition } from "~/shared/context/SharedTransition";
import { useTransitionSearchParams } from "~/shared/hooks/useTransitionSearchParams";

// ==========================================
// URL CONTRACT DEFINITION
// ==========================================
export const JOB_SETTING_TYPES = {
  JOB: "job",
} as const;
type JobSettingType = (typeof JOB_SETTING_TYPES)[keyof typeof JOB_SETTING_TYPES];

export const JOB_SETTING_TAB = {
  // JOB_QUESTIONS: "job-questions",
  JOB_INTERVIEW_PLAN: "interview-plan",
} as const;
type JobSettingTabKey = (typeof JOB_SETTING_TAB)[keyof typeof JOB_SETTING_TAB];

export const JOB_SETTING_TAB_LABELS = {
  // "job-questions": {
  //   label: "Job Questions",
  //   icon: "survey-line",
  // },
  "interview-plan": {
    label: "Interview Plan",
    icon: "git-merge-line",
  },
} as const satisfies Record<JobSettingTabKey, { label: string; icon: IconType }>;

export const JOB_DEFAULT_TAB = JOB_SETTING_TAB.JOB_INTERVIEW_PLAN;

const JOB_SEARCH_PARAMS = {
  SETTINGS: "settings",
  TAB: "tab",
  ROUND_ACTION: "roundAction",
  ROUND_ID: "roundId",
  ROUND_PHASE_ID: "roundPhaseId",
  BRAINSTORMS_TERM: "brainstormsTerm",
  BRAINSTORMS_PAGE: "brainstormsPage",
  BRAINSTORMS_LIMIT: "brainstormsLimit",
  PREVIEW: "preview",
} as const;

// ==========================================
// VALIDATION SCHEMA
// ==========================================
export const JOB_INTERVIEW_ROUND_ACTIONS = {
  CREATE: "create",
  EDIT: "edit",
} as const;
export const JOB_PREVIEW_TYPES = {
  MEMBER: "member",
  PUBLIC: "public",
} as const;
export const DEFAULT_JOB_PREVIEW_TYPE = JOB_PREVIEW_TYPES.MEMBER;
const JobSearchParamSchema = z.object({
  [JOB_SEARCH_PARAMS.SETTINGS]: z.enum([JOB_SETTING_TYPES.JOB]).optional().catch(undefined),
  [JOB_SEARCH_PARAMS.TAB]: z.string().optional().default(JOB_DEFAULT_TAB).catch(JOB_DEFAULT_TAB),
  [JOB_SEARCH_PARAMS.ROUND_ACTION]: z
    .enum([JOB_INTERVIEW_ROUND_ACTIONS.CREATE, JOB_INTERVIEW_ROUND_ACTIONS.EDIT])
    .optional()
    .catch(undefined),
  [JOB_SEARCH_PARAMS.ROUND_ID]: z.string().optional().catch(undefined),
  [JOB_SEARCH_PARAMS.ROUND_PHASE_ID]: z.string().optional().catch(undefined),
  [JOB_SEARCH_PARAMS.BRAINSTORMS_TERM]: z.string().optional().catch(undefined),
  [JOB_SEARCH_PARAMS.BRAINSTORMS_PAGE]: z.coerce.number().min(1).catch(1),
  [JOB_SEARCH_PARAMS.BRAINSTORMS_LIMIT]: PaginationLimitOptionSchema,
  [JOB_SEARCH_PARAMS.PREVIEW]: z.enum([JOB_PREVIEW_TYPES.MEMBER, JOB_PREVIEW_TYPES.PUBLIC]).optional().catch(undefined),
});

// ==========================================
// SERIALIZATION & PARSING
// ==========================================
export function getJobSearchParams(searchParams: URLSearchParams) {
  const settings = searchParams.get(JOB_SEARCH_PARAMS.SETTINGS);
  const tab = searchParams.get(JOB_SEARCH_PARAMS.TAB);
  const roundAction = searchParams.get(JOB_SEARCH_PARAMS.ROUND_ACTION);
  const roundId = searchParams.get(JOB_SEARCH_PARAMS.ROUND_ID);
  const roundPhaseId = searchParams.get(JOB_SEARCH_PARAMS.ROUND_PHASE_ID);
  const brainstormsTerm = searchParams.get(JOB_SEARCH_PARAMS.BRAINSTORMS_TERM);
  const brainstormsPage = searchParams.get(JOB_SEARCH_PARAMS.BRAINSTORMS_PAGE);
  const brainstormsLimit = searchParams.get(JOB_SEARCH_PARAMS.BRAINSTORMS_LIMIT);
  const preview = searchParams.get(JOB_SEARCH_PARAMS.PREVIEW);

  return JobSearchParamSchema.parse({
    [JOB_SEARCH_PARAMS.SETTINGS]: settings,
    [JOB_SEARCH_PARAMS.TAB]: tab,
    [JOB_SEARCH_PARAMS.ROUND_ACTION]: roundAction,
    [JOB_SEARCH_PARAMS.ROUND_ID]: roundId,
    [JOB_SEARCH_PARAMS.ROUND_PHASE_ID]: roundPhaseId,
    [JOB_SEARCH_PARAMS.BRAINSTORMS_TERM]: brainstormsTerm,
    [JOB_SEARCH_PARAMS.BRAINSTORMS_PAGE]: brainstormsPage,
    [JOB_SEARCH_PARAMS.BRAINSTORMS_LIMIT]: brainstormsLimit,
    [JOB_SEARCH_PARAMS.PREVIEW]: preview,
  });
}

// ==========================================
// URL STATE ACTIONS & HOOKS
// ==========================================
export function useJobSearchParamActions(opts = DEFAULT_NAVIGATE_OPTIONS) {
  const [, setSearchParams] = useSearchParams();

  const openSettings = React.useCallback(
    (settingType: JobSettingType = JOB_SETTING_TYPES.JOB) => {
      setSearchParams((prev) => {
        prev.set(JOB_SEARCH_PARAMS.SETTINGS, settingType);
        return prev;
      }, opts);
    },
    [setSearchParams, opts],
  );

  const closeSettings = React.useCallback(() => {
    setSearchParams((prev) => {
      prev.delete(JOB_SEARCH_PARAMS.SETTINGS);
      prev.delete(JOB_SEARCH_PARAMS.TAB);
      return prev;
    }, opts);
  }, [setSearchParams, opts]);

  const setTab = React.useCallback(
    (tab: JobSettingTabKey | (string & {})) => {
      setSearchParams((prev) => {
        prev.set(JOB_SEARCH_PARAMS.TAB, tab);
        return prev;
      }, opts);
    },
    [setSearchParams, opts],
  );

  const clearTab = React.useCallback(() => {
    setSearchParams((prev) => {
      prev.delete(JOB_SEARCH_PARAMS.TAB);
      return prev;
    }, opts);
  }, [setSearchParams, opts]);

  const openCreateRound = React.useCallback(
    (globalInterviewPhaseId?: string) => {
      setSearchParams((prev) => {
        prev.set(JOB_SEARCH_PARAMS.ROUND_ACTION, JOB_INTERVIEW_ROUND_ACTIONS.CREATE);
        prev.delete(JOB_SEARCH_PARAMS.ROUND_ID);
        if (globalInterviewPhaseId) {
          prev.set(JOB_SEARCH_PARAMS.ROUND_PHASE_ID, globalInterviewPhaseId);
        } else {
          prev.delete(JOB_SEARCH_PARAMS.ROUND_PHASE_ID);
        }
        return prev;
      }, opts);
    },
    [setSearchParams, opts],
  );

  const openEditRound = React.useCallback(
    (roundId: string) => {
      setSearchParams((prev) => {
        prev.set(JOB_SEARCH_PARAMS.ROUND_ACTION, JOB_INTERVIEW_ROUND_ACTIONS.EDIT);
        prev.set(JOB_SEARCH_PARAMS.ROUND_ID, roundId);
        return prev;
      }, opts);
    },
    [setSearchParams, opts],
  );

  const closeRoundDialog = React.useCallback(() => {
    setSearchParams((prev) => {
      prev.delete(JOB_SEARCH_PARAMS.ROUND_ACTION);
      prev.delete(JOB_SEARCH_PARAMS.ROUND_ID);
      prev.delete(JOB_SEARCH_PARAMS.ROUND_PHASE_ID);
      return prev;
    }, opts);
  }, [setSearchParams, opts]);

  const openPreview = React.useCallback(() => {
    setSearchParams((prev) => {
      prev.set(JOB_SEARCH_PARAMS.PREVIEW, DEFAULT_JOB_PREVIEW_TYPE);
      return prev;
    }, opts);
  }, [setSearchParams, opts]);

  const closePreview = React.useCallback(() => {
    setSearchParams((prev) => {
      prev.delete(JOB_SEARCH_PARAMS.PREVIEW);
      return prev;
    }, opts);
  }, [setSearchParams, opts]);

  const togglePreview = React.useCallback(() => {
    setSearchParams((prev) => {
      const hasPreview = prev.get(JOB_SEARCH_PARAMS.PREVIEW) != null;
      if (hasPreview) {
        prev.delete(JOB_SEARCH_PARAMS.PREVIEW);
      } else {
        prev.set(JOB_SEARCH_PARAMS.PREVIEW, DEFAULT_JOB_PREVIEW_TYPE);
      }
      return prev;
    }, opts);
  }, [setSearchParams, opts]);

  const actions = React.useMemo(() => {
    return {
      settings: {
        open: openSettings,
        close: closeSettings,
      },
      tab: {
        set: setTab,
        clear: clearTab,
      },
      round: {
        openCreate: openCreateRound,
        openEdit: openEditRound,
        close: closeRoundDialog,
      },
      preview: {
        open: openPreview,
        close: closePreview,
        toggle: togglePreview,
      },
    };
  }, [
    openSettings,
    closeSettings,
    setTab,
    clearTab,
    openCreateRound,
    openEditRound,
    closeRoundDialog,
    openPreview,
    closePreview,
    togglePreview,
  ]);

  return actions;
}

export function useBrainstormsTableSearchParams() {
  const [searchParams] = useSearchParams();
  const { brainstormsTerm, brainstormsPage, brainstormsLimit } = getJobSearchParams(searchParams);

  return {
    term: brainstormsTerm,
    page: brainstormsPage,
    limit: brainstormsLimit,
  };
}

export function useBrainstormsTableActions(opts: NavigateOptions = DEFAULT_NAVIGATE_OPTIONS) {
  const [_, setSearchParams] = useTransitionSearchParams();
  const [isPending] = useSharedTransition();

  const setTerm = React.useCallback(
    (term: string) => {
      setSearchParams((prev) => {
        if (term) {
          prev.set(JOB_SEARCH_PARAMS.BRAINSTORMS_TERM, term);
        } else {
          prev.delete(JOB_SEARCH_PARAMS.BRAINSTORMS_TERM);
        }

        prev.set(JOB_SEARCH_PARAMS.BRAINSTORMS_PAGE, "1");
        return prev;
      }, opts);
    },
    [setSearchParams, opts],
  );

  const clearTerm = React.useCallback(() => {
    setSearchParams((prev) => {
      prev.delete(JOB_SEARCH_PARAMS.BRAINSTORMS_TERM);

      prev.set(JOB_SEARCH_PARAMS.BRAINSTORMS_PAGE, "1");
      return prev;
    }, opts);
  }, [setSearchParams, opts]);

  const setPage = React.useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        prev.set(JOB_SEARCH_PARAMS.BRAINSTORMS_PAGE, page.toString());

        return prev;
      }, opts);
    },
    [setSearchParams, opts],
  );

  const updateLimit = React.useCallback(
    (limit: number) => {
      setSearchParams((prev) => {
        prev.set(JOB_SEARCH_PARAMS.BRAINSTORMS_LIMIT, limit.toString());

        prev.set(JOB_SEARCH_PARAMS.BRAINSTORMS_PAGE, "1");
        return prev;
      }, opts);
    },
    [setSearchParams, opts],
  );

  return React.useMemo(
    () => ({
      isPending,
      actions: {
        updateTerm: setTerm,
        clearTerm,
        updatePage: setPage,
        updateLimit,
      },
    }),
    [isPending, setTerm, clearTerm, setPage, updateLimit],
  );
}

export function useJobPreviewSearchParams() {
  const [searchParams] = useSearchParams();
  const { preview } = getJobSearchParams(searchParams);

  return {
    previewType: preview,
  };
}
